import { graphqlPost } from "api/graphqlClient";
import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { Button } from "components/Button/Button";
import { formatFileSize } from "components/Form/components/FileMeta/fileMeta";
import { SimpleToggle } from "components/Form/components/Switch/Switch";
import { getWorkspaceData } from "containers/Workspaces/Workspaces.selectors";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import "./UploadGitOptions.scss";

/**
 * Git / tracked-size settings of the container a file upload targets, resolved
 * from the Redux plain-lists.
 */
export interface UploadContainerGitSettings {
    /** 'project' | 'bundle' | 'workspace'. */
    type?: string;
    /** Container has git configured at all (branch_name set). */
    isGitEnabled: boolean;
    /** track_in_git is forced true on every record in the container. */
    forceTrackInGit: boolean;
    /** Seed value for the per-upload track_in_git toggle. */
    defaultTrackInGit: boolean;
    /** Per-record tracked-size cap in bytes (undefined = no cap). */
    maxTrackedRecordBytes?: number;
    /** Target is a bundle (is_bundle_content only applies to bundles). */
    isBundle: boolean;
    /** Seed value for the per-upload is_bundle_content toggle (bundle targets). */
    defaultIsBundleContent: boolean;
}

/** Map a raw container record (GraphQL row or the Redux workspace row) + the
 *  picked location into the dialog's git settings. Pure helper. */
const mapContainerGitSettings = (
    location: AutocompleteOption,
    container: any
): UploadContainerGitSettings => {
    const maxBytes = container.max_tracked_record_bytes;
    return {
        type: location.type,
        isGitEnabled: Boolean(container.branch_name),
        forceTrackInGit: Boolean(container.force_track_in_git),
        defaultTrackInGit: Boolean(container.default_track_in_git),
        maxTrackedRecordBytes:
            maxBytes === undefined || maxBytes === null ? undefined : Number(maxBytes),
        isBundle: location.type === "bundle",
        // is_bundle_content is a bundle-only column.
        defaultIsBundleContent: Boolean(container.default_is_bundle_content),
    };
};

/**
 * Resolve the git / tracked-size settings of the picked upload-target container.
 *
 * For a project / bundle the settings are fetched FRESH over GraphQL every time
 * the dialog opens (keyed on the container id + the `isOpen` flag), mirroring the
 * create-record dialog. Reading them from the Redux container plain-lists instead
 * was stale: those lists are only reloaded on a full page refresh, so a change to
 * a container's `default_track_in_git` (or the other git settings) made elsewhere
 * did not reach the upload dialog until the user reloaded the page. A fresh fetch
 * on open guarantees the toggle reflects the container's current default.
 *
 * The workspace root has no stable id at this call site (the Files-tab caller
 * passes a placeholder), so its settings are read from the live Redux workspace
 * record, which the websocket subscription keeps current.
 *
 * Returns `undefined` while the dialog is closed or the fetch is in flight — the
 * downstream reseed effect keys on that transition, so each open reseeds the
 * toggle from the freshly-resolved default.
 */
export const useUploadContainerSettings = (
    location?: AutocompleteOption,
    isOpen = true
): UploadContainerGitSettings | undefined => {
    const workspace = useSelector(getWorkspaceData);
    // Fresh GraphQL fetch result for the current project/bundle location, tagged
    // with the location key it belongs to so a late response for a previous
    // location is ignored.
    const [fetched, setFetched] = useState<{ key: string; container: any } | undefined>(undefined);

    const locKey = location ? `${location.type}:${location.value}` : undefined;
    const needsFetch = Boolean(
        isOpen && location && (location.type === "project" || location.type === "bundle")
    );

    useEffect(() => {
        if (!needsFetch || !location) {
            setFetched(undefined);
            return;
        }
        const type = location.type as string;
        const value = location.value;
        const key = `${type}:${value}`;
        let cancelled = false;
        const bundleField = type === "bundle" ? "default_is_bundle_content" : "";
        graphqlPost({
            query: `query ($id: String!) {
                ${type}(id: $id) {
                    branch_name
                    force_track_in_git
                    default_track_in_git
                    max_tracked_record_bytes
                    ${bundleField}
                }
            }`,
            variables: { id: value },
        })
            .then((data: any) => {
                if (!cancelled) setFetched({ key, container: data?.data?.[type] ?? null });
            })
            .catch(() => {
                if (!cancelled) setFetched({ key, container: null });
            });
        return () => {
            cancelled = true;
        };
    }, [needsFetch, locKey]); // eslint-disable-line react-hooks/exhaustive-deps

    return useMemo(() => {
        if (!isOpen || !location) return undefined;
        if (location.type === "workspace") {
            return workspace ? mapContainerGitSettings(location, workspace) : undefined;
        }
        // project / bundle: only use the fetch once it matches the current
        // location (undefined while in flight).
        if (!fetched || fetched.key !== `${location.type}:${location.value}` || !fetched.container) {
            return undefined;
        }
        return mapContainerGitSettings(location, fetched.container);
    }, [isOpen, location, fetched, workspace]);
};

/**
 * The effective `track_in_git` value to send with an upload, given the raw
 * toggle state. A container that forces tracking (`force_track_in_git`) clamps
 * the value to `true` regardless of the toggle — the toggle is non-overridable.
 * Pure — unit-tested in `UploadGitOptions.test.ts`.
 */
export const resolveEffectiveTrackInGit = (
    settings: UploadContainerGitSettings | undefined,
    toggle: boolean
): boolean => (settings?.forceTrackInGit ? true : toggle);

/**
 * Whether a create-menu upload must be blocked up front because a file would be
 * git-tracked yet exceeds the target container's tracked-size cap (§4 of the
 * upload-dialog spec). True only when the container is git-enabled, has a cap
 * set, the file will actually be tracked, and the largest file strictly exceeds
 * the cap (a file exactly at the limit is allowed). Pure — unit-tested in
 * `UploadGitOptions.test.ts`.
 */
export const computeUploadOverLimit = (
    settings: UploadContainerGitSettings | undefined,
    effectiveTrackInGit: boolean,
    largestFileBytes: number
): boolean =>
    Boolean(
        settings?.isGitEnabled &&
            settings?.maxTrackedRecordBytes != null &&
            effectiveTrackInGit &&
            largestFileBytes > settings.maxTrackedRecordBytes
    );

/**
 * The seed value for the per-upload `is_bundle_content` toggle: the target
 * bundle's `default_is_bundle_content` (false for non-bundle targets). Pure —
 * unit-tested in `UploadGitOptions.test.ts`.
 */
export const resolveInitialIsBundleContent = (
    settings: UploadContainerGitSettings | undefined
): boolean => Boolean(settings?.isBundle && settings?.defaultIsBundleContent);

/** Toggle state + derived over-limit block for a create-menu file upload. */
export interface UploadGitOptionsState {
    settings?: UploadContainerGitSettings;
    /**
     * Whether the dialog/options block should render at all — the target
     * offers at least one choice: a git-enabled container (track_in_git) or a
     * bundle (is_bundle_content). Drives §3 "no choice ⇒ no dialog".
     */
    showDialog: boolean;
    /** Whether the track_in_git row should render (git-enabled target). */
    showGitOptions: boolean;
    /** Whether the is_bundle_content row should render (bundle target). */
    showBundleContent: boolean;
    /** Effective track_in_git value to send with the upload (clamped when forced). */
    trackInGit: boolean;
    setTrackInGit: (value: boolean) => void;
    /** is_bundle_content value to send with the upload (bundle targets). */
    isBundleContent: boolean;
    setIsBundleContent: (value: boolean) => void;
    /** At least one file exceeds the tracked-size cap while tracking is on. */
    overLimit: boolean;
    /** Confirm must be blocked (over-limit while tracking). */
    blockConfirm: boolean;
    /** Size in bytes of the largest file in the batch. */
    largestFileBytes: number;
}

/**
 * Owns the per-upload `track_in_git` toggle state and derives the client-side
 * over-limit block (§4 of the upload-dialog spec): when the target container has
 * a `max_tracked_record_bytes` cap and the file would be git-tracked, a file
 * larger than the cap is refused by the backend — so we block confirm up front
 * and explain the ways forward, instead of wasting the upload round-trip.
 *
 * Toggle defaults are seeded from the container whenever the *picked container*
 * changes (keyed on `containerId`, not the settings object identity, so a
 * websocket-driven store refresh doesn't clobber a choice the user just made).
 */
export const useUploadGitOptions = (
    containerId: string | undefined,
    settings: UploadContainerGitSettings | undefined,
    fileSizes: number[]
): UploadGitOptionsState => {
    const [trackInGit, setTrackInGit] = useState<boolean>(false);
    const [isBundleContent, setIsBundleContent] = useState<boolean>(false);

    const settingsLoaded = settings != null;
    useEffect(() => {
        setTrackInGit(settings ? settings.forceTrackInGit || settings.defaultTrackInGit : false);
        setIsBundleContent(resolveInitialIsBundleContent(settings));
        // Reseed only on container change / first settings load — deliberately
        // not on every `settings` object identity change (would reset the user).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerId, settingsLoaded]);

    // track_in_git is non-overridable when the container forces it.
    const effectiveTrackInGit = resolveEffectiveTrackInGit(settings, trackInGit);

    const largestFileBytes = fileSizes.length > 0 ? Math.max(...fileSizes) : 0;

    const overLimit = computeUploadOverLimit(settings, effectiveTrackInGit, largestFileBytes);

    return {
        settings,
        showDialog: Boolean(settings?.isGitEnabled || settings?.isBundle),
        showGitOptions: Boolean(settings?.isGitEnabled),
        showBundleContent: Boolean(settings?.isBundle),
        trackInGit: effectiveTrackInGit,
        setTrackInGit,
        isBundleContent,
        setIsBundleContent,
        overLimit,
        blockConfirm: overLimit,
        largestFileBytes,
    };
};

/**
 * The git-options block rendered inside the "Upload file to:" dialog, between the
 * location picker and the button row. Shown only when the picked container is
 * git-enabled. Renders the `track_in_git` toggle (forced on + disabled when the
 * container forces tracking) and, when the file is over the container's
 * tracked-size cap, an explaining block that offers the ways forward.
 */
export const UploadGitOptions = ({ state }: { state: UploadGitOptionsState }) => {
    const {
        settings,
        showDialog,
        showGitOptions,
        showBundleContent,
        trackInGit,
        setTrackInGit,
        isBundleContent,
        setIsBundleContent,
        overLimit,
        largestFileBytes,
    } = state;

    if (!settings || !showDialog) return null;

    const forced = settings.forceTrackInGit;

    return (
        <div className="uploadGitOptions">
            {showGitOptions && (
                <div className="uploadGitOptions__row">
                    <label className="uploadGitOptions__label" htmlFor="upload-track-in-git">
                        Track in git
                        {forced && (
                            <span className="uploadGitOptions__hint">
                                {" "}
                                (forced by {settings.type})
                            </span>
                        )}
                    </label>
                    <SimpleToggle
                        identifier="upload-track-in-git"
                        value={trackInGit}
                        isReadOnly={forced}
                        additionalAttributes={{
                            onChange: (checked: boolean) => setTrackInGit(checked),
                        }}
                    />
                </div>
            )}

            {showBundleContent && (
                <div className="uploadGitOptions__row">
                    <label className="uploadGitOptions__label" htmlFor="upload-is-bundle-content">
                        Bundle content
                        <span className="uploadGitOptions__hint">
                            {" "}
                            (include in bundle releases)
                        </span>
                    </label>
                    <SimpleToggle
                        identifier="upload-is-bundle-content"
                        value={isBundleContent}
                        additionalAttributes={{
                            onChange: (checked: boolean) => setIsBundleContent(checked),
                        }}
                    />
                </div>
            )}

            {overLimit && settings.maxTrackedRecordBytes != null && (
                <div className="uploadGitOptions__warning" role="alert">
                    <div className="uploadGitOptions__warningText">
                        The largest selected file ({formatFileSize(largestFileBytes)}) exceeds this{" "}
                        {settings.type}&rsquo;s tracked-size limit of{" "}
                        {formatFileSize(settings.maxTrackedRecordBytes)}. Git-tracked records cannot
                        exceed the limit, so the upload is blocked while &ldquo;Track in git&rdquo;
                        is on. To continue you can:
                        <ul className="uploadGitOptions__warningList">
                            <li>
                                turn off &ldquo;Track in git&rdquo; for this upload — the file is
                                still added, it just is not tracked in git; or
                            </li>
                            <li>
                                raise this {settings.type}&rsquo;s tracked-size limit in its git
                                settings (this changes the limit for every record in this{" "}
                                {settings.type}); or
                            </li>
                            <li>upload to a different project or bundle.</li>
                        </ul>
                    </div>
                    {!forced && (
                        <Button
                            className="uploadGitOptions__disableButton"
                            variation="dark"
                            size="small"
                            label={'Turn off "Track in git"'}
                            onClick={() => setTrackInGit(false)}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
